/**
 * BLE Sync Context
 * Manages AutoSend service lifecycle and coordinates with Manual Sync
 */

import React, {createContext, useState, useRef, useEffect, useCallback} from 'react';
import {Platform, PermissionsAndroid} from 'react-native';
import {getData, storeData} from '../utils/GeneralStorage';
import {KEYS} from '../utils/Constants';
import {DEVICE_TYPES} from '../utils/BLEDeviceTypes';
import {DLog} from '../utils/HelperMethods';
import {fetchApi} from '../utils/ApiHelper';
import {HTTP_METHODS, BLOOD_SUGAR_LEVEL} from '../utils/URLConstants';
import {syncReadingsToHealthKit} from '../healthkit/HealthKitSync';

// Sync states
export const BLE_SYNC_STATE = {
  IDLE: 'IDLE',
  AUTOSEND_LISTENING: 'AUTOSEND_LISTENING',
  AUTOSEND_RECEIVING: 'AUTOSEND_RECEIVING',
  AUTOSEND_SUCCESS: 'AUTOSEND_SUCCESS',
  MANUAL_SYNCING: 'MANUAL_SYNCING',
};

export const bleSyncContext = createContext();
const {Provider} = bleSyncContext;

const BLESyncContext = props => {
  // State
  const [syncState, setSyncState] = useState(BLE_SYNC_STATE.IDLE);
  const [isReceivingData, setIsReceivingData] = useState(false);
  const [lastReading, setLastReading] = useState(null);
  // Version counter that increments when new readings are synced - used by HomePage to know when to refresh
  const [readingsVersion, setReadingsVersion] = useState(0);
  // New state for AutoSend-first UI
  const [todayReadings, setTodayReadings] = useState([]);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncStats, setSyncStats] = useState({todayCount: 0, totalSynced: 0});

  // Refs for service instances
  const bleManagerRef = useRef(null);
  const autoSendServiceRef = useRef(null);
  const receivingTimeoutRef = useRef(null);
  const manualSyncLockRef = useRef(false);
  const isInitializedRef = useRef(false);
  // Track recently paired devices - passed to AutoSend when service starts
  const recentlyPairedDevicesRef = useRef(new Set());

  // BLE semaphore for mutual exclusion between AutoSend and Manual Sync
  const bleSemaphoreRef = useRef({
    isLocked: false,
    lockOwner: null,
    lockTimestamp: null,
  });

  // Acquire BLE lock with timeout
  const acquireBLELock = async (owner, timeoutMs = 30000) => {
    const startTime = Date.now();
    while (bleSemaphoreRef.current.isLocked) {
      // Check for timeout
      if (Date.now() - startTime > timeoutMs) {
        DLog('[BLESyncContext] Lock timeout, forcing release from:', bleSemaphoreRef.current.lockOwner);
        releaseBLELock();
        break;
      }
      // Also check if lock is stale (held for more than 60 seconds)
      if (bleSemaphoreRef.current.lockTimestamp &&
          Date.now() - bleSemaphoreRef.current.lockTimestamp > 60000) {
        DLog('[BLESyncContext] Stale lock detected, forcing release from:', bleSemaphoreRef.current.lockOwner);
        releaseBLELock();
        break;
      }
      // Wait and try again
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    bleSemaphoreRef.current = {
      isLocked: true,
      lockOwner: owner,
      lockTimestamp: Date.now(),
    };
    DLog('[BLESyncContext] BLE lock acquired by:', owner);
    return true;
  };

  // Release BLE lock
  const releaseBLELock = () => {
    const owner = bleSemaphoreRef.current.lockOwner;
    bleSemaphoreRef.current = {
      isLocked: false,
      lockOwner: null,
      lockTimestamp: null,
    };
    DLog('[BLESyncContext] BLE lock released by:', owner);
  };

  // Get stored devices (inline to avoid circular dependency)
  const getStoredDevices = async () => {
    try {
      const devicesJSON = await getData(KEYS.bluetoothDeviceList);
      if (devicesJSON) {
        return JSON.parse(devicesJSON);
      }
      return [];
    } catch (error) {
      DLog('[BLESyncContext] Error getting stored devices:', error);
      return [];
    }
  };

  // Check if device is ACON type
  const isAconDevice = device => {
    return (
      device.deviceType === DEVICE_TYPES.ACON ||
      device.deviceType === 'ACON' ||
      device.deviceType === 1 ||
      device.name?.toLowerCase().includes('diathrive') ||
      device.name?.toLowerCase().includes('express')
    );
  };

  // Initialize BLE Manager
  const initializeBLEManager = useCallback(async () => {
    if (bleManagerRef.current) return bleManagerRef.current;

    try {
      DLog('[BLESyncContext] Initializing BLE Manager...');
      // Dynamic import to avoid circular dependencies
      const {RealBLEManager} = await import('../bluetooth/RealBLEManager');
      const bleManager = new RealBLEManager();
      await bleManager.initialize();
      bleManagerRef.current = bleManager;
      DLog('[BLESyncContext] BLE Manager initialized');
      return bleManager;
    } catch (error) {
      DLog('[BLESyncContext] BLE Manager init error:', error);
      return null;
    }
  }, []);

  // Generate a unique key for a reading (for deduplication)
  const generateReadingKey = reading => {
    // Use datetime + glucose value as unique identifier
    return `${reading.dateTimeOfReading}_${reading.glucoseValue}`;
  };

  // Check if a reading is from today
  const isReadingFromToday = reading => {
    const today = new Date();
    const readingDate = new Date(reading.dateTimeOfReading);
    return (
      readingDate.getDate() === today.getDate() &&
      readingDate.getMonth() === today.getMonth() &&
      readingDate.getFullYear() === today.getFullYear()
    );
  };

  // Load today's readings from storage
  const loadTodayReadings = async () => {
    try {
      const devices = await getStoredDevices();
      const allTodayReadings = [];

      for (const device of devices) {
        if (!isAconDevice(device)) continue;

        const storageKey = device.id;
        const dataJSON = await getData(storageKey);
        if (!dataJSON) continue;

        const data = JSON.parse(dataJSON);
        for (const key in data) {
          const reading = data[key];
          if (isReadingFromToday(reading)) {
            allTodayReadings.push(reading);
          }
        }
      }

      // Sort by datetime (newest first)
      allTodayReadings.sort(
        (a, b) => new Date(b.dateTimeOfReading) - new Date(a.dateTimeOfReading),
      );

      setTodayReadings(allTodayReadings);
      setSyncStats(prev => ({
        ...prev,
        todayCount: allTodayReadings.length,
      }));
    } catch (error) {
      // Silent - non-critical
    }
  };

  // Store auto-send reading for later API sync
  const storeAutoSendReading = async reading => {
    try {
      const userId = await getData(KEYS.userId);

      // Use device.id as the storage key (consistent with HomePage)
      const devices = await getStoredDevices();
      const device = devices?.find(d => d.id === reading.deviceId);
      // Use device.id for storage key, fall back to reading.deviceId
      const storageKey = device?.id || reading.deviceId || 'AutoSendReadings';

      const existingDataJSON = await getData(storageKey);
      const existingData = existingDataJSON ? JSON.parse(existingDataJSON) : {};

      // Generate unique key for deduplication
      const entryKey = generateReadingKey(reading);

      // Check for duplicate before storing
      if (existingData[entryKey]) {
        // Check if this reading was already synced to API
        const existingReading = existingData[entryKey];

        if (existingReading.isSynced === 1) {
          // Already synced to API - just update UI state
          const syncTime = new Date();
          setLastSyncTime(syncTime);
          await storeData(KEYS.lastSyncedTime, syncTime.toISOString());
          console.log('[BLESyncContext] Duplicate reading (already synced), updated lastSyncTime:', syncTime.toISOString());

          // Still update todayReadings for duplicates - they should show in the UI
          if (isReadingFromToday(reading)) {
            setTodayReadings(prev => {
              const exists = prev.some(
                r =>
                  r.dateTimeOfReading === reading.dateTimeOfReading &&
                  r.glucoseValue === reading.glucoseValue,
              );
              if (exists) return prev;
              return [{...reading, isSynced: 1}, ...prev];
            });
          }
          return; // Skip - already synced
        }

        // Reading exists locally but NOT synced to API - try to sync now
        console.log('[BLESyncContext] Found unsynced reading in storage, syncing to API...');
        try {
          const params = {
            measureAt: reading.dateTimeOfReading,
            bloodSugarCount: reading.glucoseValue,
          };
          const res = await fetchApi(HTTP_METHODS.POST, BLOOD_SUGAR_LEVEL, params);
          if (res.status === 200) {
            existingData[entryKey].isSynced = 1;
            await storeData(storageKey, JSON.stringify(existingData));
            console.log('[BLESyncContext] Successfully synced previously unsynced reading to API');
          }
        } catch (apiError) {
          if (apiError?.message === 'Duplicate data.') {
            // Already in API, mark as synced
            existingData[entryKey].isSynced = 1;
            await storeData(storageKey, JSON.stringify(existingData));
            console.log('[BLESyncContext] Reading already in API, marked as synced');
          } else {
            console.log('[BLESyncContext] API sync failed for unsynced reading:', apiError?.message);
          }
        }

        // Update UI state
        const syncTime = new Date();
        setLastSyncTime(syncTime);
        await storeData(KEYS.lastSyncedTime, syncTime.toISOString());

        if (isReadingFromToday(reading)) {
          setTodayReadings(prev => {
            const exists = prev.some(
              r =>
                r.dateTimeOfReading === reading.dateTimeOfReading &&
                r.glucoseValue === reading.glucoseValue,
            );
            if (exists) return prev;
            return [{...reading, isSynced: existingData[entryKey].isSynced}, ...prev];
          });
        }

        setReadingsVersion(v => v + 1);
        return;
      }

      existingData[entryKey] = {
        ...reading,
        userId,
        isSynced: 0,
      };

      await storeData(storageKey, JSON.stringify(existingData));

      // Mirror the new reading into Apple Health (no-op unless the user
      // opted in; must never block or fail the sync flow) — #65
      syncReadingsToHealthKit([
        {
          glucoseValueMgdl: reading.glucoseValue,
          measuredAtIso: reading.dateTimeOfReading,
          source: 'meter',
        },
      ]);

      // Immediately sync to API
      try {
        const params = {
          measureAt: reading.dateTimeOfReading,
          bloodSugarCount: reading.glucoseValue,
        };
        const res = await fetchApi(HTTP_METHODS.POST, BLOOD_SUGAR_LEVEL, params);
        if (res.status === 200) {
          existingData[entryKey].isSynced = 1;
          await storeData(storageKey, JSON.stringify(existingData));

          // Update lastSyncTime
          const syncTime = new Date();
          setLastSyncTime(syncTime);
          await storeData(KEYS.lastSyncedTime, syncTime.toISOString());
          console.log('[BLESyncContext] API sync success, updated lastSyncTime:', syncTime.toISOString());

          // Update todayReadings if this reading is from today
          if (isReadingFromToday(reading)) {
            setTodayReadings(prev => {
              // Add to beginning (newest first), avoid duplicates
              const exists = prev.some(
                r =>
                  r.dateTimeOfReading === reading.dateTimeOfReading &&
                  r.glucoseValue === reading.glucoseValue,
              );
              if (exists) return prev;
              return [{...reading, isSynced: 1}, ...prev];
            });
            setSyncStats(prev => ({
              ...prev,
              todayCount: prev.todayCount + 1,
              totalSynced: prev.totalSynced + 1,
            }));
          }

          // Increment version to notify UI to refresh
          setReadingsVersion(v => v + 1);
        }
      } catch (apiError) {
        if (apiError?.message === 'Duplicate data.') {
          // Already synced, mark as synced
          existingData[entryKey].isSynced = 1;
          await storeData(storageKey, JSON.stringify(existingData));

          // Still update lastSyncTime - a sync happened even if readings were duplicates
          const syncTime = new Date();
          setLastSyncTime(syncTime);
          await storeData(KEYS.lastSyncedTime, syncTime.toISOString());
          console.log('[BLESyncContext] Duplicate in API, updated lastSyncTime:', syncTime.toISOString());
        } else {
          DLog('[BLESyncContext] API sync failed, will retry on HomePage:', apiError?.message);
        }
      }
    } catch (error) {
      DLog('[BLESyncContext] Failed to store AutoSend reading:', error);
    }
  };

  // Start AutoSend listener for saved ACON devices
  const startAutoSend = useCallback(async () => {
    // Don't start if manual sync is in progress
    if (manualSyncLockRef.current || bleSemaphoreRef.current.isLocked) {
      DLog('[BLESyncContext] startAutoSend blocked by lock');
      return;
    }

    try {
      // Get saved devices
      const devices = await getStoredDevices();
      const aconDevices = devices?.filter(isAconDevice) || [];

      if (aconDevices.length === 0) {
        return;
      }

      // If service already exists, just add any new devices to monitoring
      if (autoSendServiceRef.current) {
        DLog('[BLESyncContext] AutoSend service exists, adding devices to monitoring');
        const existingService = autoSendServiceRef.current;
        const monitoredDevices = existingService.getMonitoredDevices();
        const monitoredIds = new Set(monitoredDevices.map(d => d.id));

        // Mark any recently paired devices
        for (const deviceId of recentlyPairedDevicesRef.current) {
          existingService.markDeviceAsRecentlyPaired(deviceId);
        }
        recentlyPairedDevicesRef.current.clear();

        // Add any devices not already being monitored
        let addedCount = 0;
        for (const device of aconDevices) {
          if (!monitoredIds.has(device.id)) {
            try {
              await existingService.startMonitoring({
                ...device,
                deviceType: 'ACON',
                isPaired: true,
              });
              addedCount++;
              DLog('[BLESyncContext] Added device to monitoring:', device.name);
            } catch (error) {
              DLog('[BLESyncContext] Failed to add device to monitoring:', device.name, error.message);
            }
          }
        }

        // Ensure scanning is started (it may have stopped)
        if (!existingService.isMonitoring()) {
          DLog('[BLESyncContext] AutoSend not scanning, restarting...');
          // Recreate service to restart scanning
          await existingService.stopAll();
          autoSendServiceRef.current = null;
          // Fall through to create new service
        } else {
          if (addedCount > 0) {
            console.log('[AutoSend] ✅ Added', addedCount, 'new device(s) to monitoring');
          }
          setSyncState(BLE_SYNC_STATE.AUTOSEND_LISTENING);
          return;
        }
      }

      // Initialize BLE manager if needed
      const bleManager = await initializeBLEManager();
      if (!bleManager) {
        DLog('[BLESyncContext] Failed to initialize BLE manager');
        return;
      }

      // Dynamic import AutoSend service
      const {AconAutoSendService} = await import('../bluetooth/sync/AconAutoSendService');

      // Create AutoSend service with callbacks
      const autoSendService = new AconAutoSendService(bleManager, {
        // Called immediately when data starts arriving (before parsing)
        onReceivingData: () => {
          console.log('[BLESyncContext] 📶 Data arriving from meter...');
          setIsReceivingData(true);
          setSyncState(BLE_SYNC_STATE.AUTOSEND_RECEIVING);
        },
        onReadingReceived: reading => {
          console.log('[BLESyncContext] Received reading:', reading.glucoseValue, 'mg/dL from', reading.dateTimeOfReading);
          // Only update lastReading if this reading is newer than the current one
          setLastReading(prev => {
            if (!prev) return reading;
            const prevTime = new Date(prev.dateTimeOfReading).getTime();
            const newTime = new Date(reading.dateTimeOfReading).getTime();
            return newTime > prevTime ? reading : prev;
          });
          // Keep receiving state active
          setIsReceivingData(true);
          setSyncState(BLE_SYNC_STATE.AUTOSEND_RECEIVING);

          // Clear previous timeout
          if (receivingTimeoutRef.current) {
            clearTimeout(receivingTimeoutRef.current);
          }

          // After 3 seconds of no data, show success state briefly then go back to listening
          receivingTimeoutRef.current = setTimeout(() => {
            setIsReceivingData(false);
            setSyncState(BLE_SYNC_STATE.AUTOSEND_SUCCESS);
            // Show success for 2 seconds, then back to listening
            setTimeout(() => {
              setSyncState(BLE_SYNC_STATE.AUTOSEND_LISTENING);
            }, 2000);
          }, 3000);

          // Store the reading
          storeAutoSendReading(reading);
        },
        onError: error => {
          DLog('[BLESyncContext] AutoSend error:', error.message);
        },
        onConnectionStateChange: (deviceId, isConnected) => {
          // Silent - only used for internal tracking
        },
        // Block AutoSend connections when manual sync is in progress
        shouldConnect: () => {
          if (manualSyncLockRef.current) {
            DLog('[BLESyncContext] AutoSend blocked: manual sync lock active');
            return false;
          }
          if (bleSemaphoreRef.current.isLocked) {
            DLog('[BLESyncContext] AutoSend blocked: BLE semaphore locked by', bleSemaphoreRef.current.lockOwner);
            return false;
          }
          return true;
        },
      });

      autoSendServiceRef.current = autoSendService;

      // Mark any recently paired devices in the service
      // This prevents AutoSend from immediately connecting after pairing
      for (const deviceId of recentlyPairedDevicesRef.current) {
        autoSendService.markDeviceAsRecentlyPaired(deviceId);
      }
      recentlyPairedDevicesRef.current.clear();

      // Start monitoring all ACON devices
      for (const device of aconDevices) {
        try {
          await autoSendService.startMonitoring({
            ...device,
            deviceType: 'ACON',
            isPaired: true,
          });
        } catch (error) {
          DLog('[BLESyncContext] Failed to monitor device:', device.name, error.message);
        }
      }

      setSyncState(BLE_SYNC_STATE.AUTOSEND_LISTENING);
      console.log('[AutoSend] ✅ Listening for', aconDevices.length, 'device(s)');
    } catch (error) {
      DLog('[BLESyncContext] startAutoSend error:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- storeAutoSendReading is recreated each render; adding it would destabilize startAutoSend and restart the AutoSend lifecycle effect every render
  }, [initializeBLEManager]);

  // Stop AutoSend listener
  const stopAutoSend = useCallback(async () => {
    if (autoSendServiceRef.current) {
      DLog('[BLESyncContext] Stopping AutoSend');
      try {
        await autoSendServiceRef.current.stopAll();
      } catch (error) {
        DLog('[BLESyncContext] Error stopping AutoSend:', error);
      }
      autoSendServiceRef.current = null;
    }

    if (receivingTimeoutRef.current) {
      clearTimeout(receivingTimeoutRef.current);
      receivingTimeoutRef.current = null;
    }

    setIsReceivingData(false);
    setSyncState(BLE_SYNC_STATE.IDLE);
  }, []);

  // Pause AutoSend (before manual sync)
  const pauseAutoSend = useCallback(async () => {
    if (autoSendServiceRef.current) {
      DLog('[BLESyncContext] Pausing AutoSend');
      try {
        await autoSendServiceRef.current.stopAll();
      } catch (error) {
        DLog('[BLESyncContext] Error pausing AutoSend:', error);
      }
      autoSendServiceRef.current = null;
      setIsReceivingData(false);
    }
  }, []);

  // Resume AutoSend (after manual sync)
  const resumeAutoSend = useCallback(async () => {
    if (!manualSyncLockRef.current) {
      DLog('[BLESyncContext] Resuming AutoSend');
      await startAutoSend();
    }
  }, [startAutoSend]);

  // Clear device verification (for re-pairing flow)
  // Call this when user is removing or re-pairing a device
  // Prevents AutoSend from trying to connect during pairing
  const clearDeviceVerification = useCallback((deviceId) => {
    if (autoSendServiceRef.current) {
      autoSendServiceRef.current.clearDeviceVerification(deviceId);
    }
  }, []);

  // Mark a device as recently paired - AutoSend will ignore it briefly
  // This prevents connecting immediately after pairing when meter isn't ready
  const markDeviceAsRecentlyPaired = useCallback((deviceId) => {
    // Store in ref so it persists until AutoSend service is created
    recentlyPairedDevicesRef.current.add(deviceId);
    // Also mark in service if it exists
    if (autoSendServiceRef.current) {
      autoSendServiceRef.current.markDeviceAsRecentlyPaired(deviceId);
    }
  }, []);

  // Request lock for manual sync (pauses AutoSend)
  const requestManualSyncLock = useCallback(async () => {
    DLog('[BLESyncContext] Manual sync lock requested');
    // Acquire semaphore first
    await acquireBLELock('ManualSync');
    manualSyncLockRef.current = true;
    await pauseAutoSend();
    setSyncState(BLE_SYNC_STATE.MANUAL_SYNCING);
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- acquireBLELock is a per-render function over refs only; adding it would destabilize this callback's identity without behavior benefit
  }, [pauseAutoSend]);

  // Release manual sync lock (resumes AutoSend)
  const releaseManualSyncLock = useCallback(async () => {
    DLog('[BLESyncContext] Manual sync lock released');
    manualSyncLockRef.current = false;
    // Release semaphore
    releaseBLELock();
    setSyncState(BLE_SYNC_STATE.IDLE);
    // Small delay before resuming to let cleanup complete
    setTimeout(async () => {
      await resumeAutoSend();
    }, 500);
  }, [resumeAutoSend]);

  // Force sync - manually trigger a full sync for ACON devices
  // This pauses AutoSend, does a manual sync, then resumes AutoSend
  const forceSync = useCallback(
    async deviceId => {
      DLog('[BLESyncContext] Force sync requested for device:', deviceId);

      try {
        // Acquire lock and pause AutoSend
        await acquireBLELock('ForceSync');
        manualSyncLockRef.current = true;
        await pauseAutoSend();
        setSyncState(BLE_SYNC_STATE.MANUAL_SYNCING);

        // Get BLE manager
        const bleManager = await initializeBLEManager();
        if (!bleManager) {
          throw new Error('Failed to initialize BLE manager');
        }

        // Import sync service
        const {AconManualSync} = await import(
          '../bluetooth/sync/AconManualSync'
        );

        // Get device info
        const devices = await getStoredDevices();
        const device = devices?.find(d => d.id === deviceId);
        if (!device) {
          throw new Error('Device not found');
        }

        // Create sync service and run sync
        const syncService = new AconManualSync(bleManager);
        const result = await syncService.startSync({
          ...device,
          deviceType: 'ACON',
          isPaired: true,
        });

        DLog('[BLESyncContext] Force sync result:', result);

        // Store readings
        if (result.success && result.readings?.length > 0) {
          const userId = await getData(KEYS.userId);
          const storageKey = device.id;
          const existingDataJSON = await getData(storageKey);
          const existingData = existingDataJSON
            ? JSON.parse(existingDataJSON)
            : {};

          let newReadingsCount = 0;
          const readingsForHealthKit = [];

          for (const reading of result.readings) {
            const entryKey = generateReadingKey(reading);
            if (!existingData[entryKey]) {
              existingData[entryKey] = {
                ...reading,
                userId,
                isSynced: 0,
              };
              readingsForHealthKit.push({
                glucoseValueMgdl: reading.glucoseValue,
                measuredAtIso: reading.dateTimeOfReading,
                source: 'meter',
              });

              // Try to sync to API
              try {
                const params = {
                  measureAt: reading.dateTimeOfReading,
                  bloodSugarCount: reading.glucoseValue,
                };
                const res = await fetchApi(
                  HTTP_METHODS.POST,
                  BLOOD_SUGAR_LEVEL,
                  params,
                );
                if (res.status === 200) {
                  existingData[entryKey].isSynced = 1;
                  newReadingsCount++;

                  // Update todayReadings if this reading is from today
                  if (isReadingFromToday(reading)) {
                    setTodayReadings(prev => {
                      const exists = prev.some(
                        r =>
                          r.dateTimeOfReading === reading.dateTimeOfReading &&
                          r.glucoseValue === reading.glucoseValue,
                      );
                      if (exists) return prev;
                      return [{...reading, isSynced: 1}, ...prev];
                    });
                  }
                }
              } catch (apiError) {
                if (apiError?.message === 'Duplicate data.') {
                  existingData[entryKey].isSynced = 1;
                }
              }
            }
          }

          await storeData(storageKey, JSON.stringify(existingData));

          // Mirror newly stored readings into Apple Health (no-op unless the
          // user opted in; must never block or fail the sync flow) — #65
          if (readingsForHealthKit.length > 0) {
            syncReadingsToHealthKit(readingsForHealthKit);
          }

          // Update sync stats
          if (newReadingsCount > 0) {
            const syncTime = new Date();
            setLastSyncTime(syncTime);
            await storeData(KEYS.lastSyncedTime, syncTime.toISOString());
            setSyncStats(prev => ({
              ...prev,
              totalSynced: prev.totalSynced + newReadingsCount,
            }));
            setReadingsVersion(v => v + 1);
          }

          DLog(
            '[BLESyncContext] Force sync completed, new readings:',
            newReadingsCount,
          );

          // Refresh todayReadings from storage to ensure state is in sync
          await loadTodayReadings();

          return {success: true, newReadingsCount};
        }

        // Even if no new readings, refresh todayReadings to ensure state is current
        await loadTodayReadings();

        return {success: true, newReadingsCount: 0};
      } catch (error) {
        DLog('[BLESyncContext] Force sync error:', error);
        return {success: false, error: error.message};
      } finally {
        // Release lock and resume AutoSend
        manualSyncLockRef.current = false;
        releaseBLELock();
        setSyncState(BLE_SYNC_STATE.IDLE);
        // Small delay before resuming
        setTimeout(async () => {
          await resumeAutoSend();
        }, 500);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- acquireBLELock/loadTodayReadings are per-render functions over refs/state setters; adding them would destabilize forceSync's identity
    [initializeBLEManager, pauseAutoSend, resumeAutoSend],
  );

  // Auto-start AutoSend on mount if saved ACON devices exist
  useEffect(() => {
    if (isInitializedRef.current || Platform.OS === 'web') {
      return;
    }

    isInitializedRef.current = true;

    const initAutoSend = async () => {
      // Delay to let app initialize
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Check and request BLE permissions on Android 12+
      if (Platform.OS === 'android' && Platform.Version >= 31) {
        try {
          const scanPermission = await PermissionsAndroid.check(
            PermissionsAndroid.PERMISSIONS.BLUETOOTH_SCAN,
          );
          const connectPermission = await PermissionsAndroid.check(
            PermissionsAndroid.PERMISSIONS.BLUETOOTH_CONNECT,
          );

          if (!scanPermission || !connectPermission) {
            DLog('[BLESyncContext] Requesting BLE permissions...');
            const results = await PermissionsAndroid.requestMultiple([
              PermissionsAndroid.PERMISSIONS.BLUETOOTH_SCAN,
              PermissionsAndroid.PERMISSIONS.BLUETOOTH_CONNECT,
            ]);

            const scanGranted = results[PermissionsAndroid.PERMISSIONS.BLUETOOTH_SCAN] === PermissionsAndroid.RESULTS.GRANTED;
            const connectGranted = results[PermissionsAndroid.PERMISSIONS.BLUETOOTH_CONNECT] === PermissionsAndroid.RESULTS.GRANTED;

            if (!scanGranted || !connectGranted) {
              DLog('[BLESyncContext] BLE permissions not granted, skipping AutoSend init');
              return;
            }
            DLog('[BLESyncContext] BLE permissions granted');
          }
        } catch (permError) {
          DLog('[BLESyncContext] Permission check error:', permError);
          return;
        }
      }

      // Load last sync time
      try {
        const savedSyncTime = await getData(KEYS.lastSyncedTime);
        if (savedSyncTime) {
          setLastSyncTime(new Date(savedSyncTime));
        }
      } catch (e) {
        // Silent - non-critical
      }

      // Sync OS-paired devices to recover from cleared AsyncStorage
      // This ensures devices paired at the OS level are available for AutoSend
      try {
        const bleManager = await initializeBLEManager();
        if (bleManager) {
          const {PairingService} = await import('../bluetooth/pairing/PairingService');
          const pairingService = new PairingService(bleManager);
          const addedCount = await pairingService.syncOSPairedDevices();
          if (addedCount > 0) {
            DLog('[BLESyncContext] Auto-recovered', addedCount, 'OS-paired devices');
          }
        }
      } catch (error) {
        DLog('[BLESyncContext] Error syncing OS paired devices:', error);
      }

      // Load today's readings
      await loadTodayReadings();

      const devices = await getStoredDevices();
      const hasAconDevices = devices?.some(isAconDevice) || false;

      if (hasAconDevices) {
        await startAutoSend();
      }
    };

    initAutoSend();

    // Cleanup on unmount
    return () => {
      stopAutoSend();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only AutoSend lifecycle; loadTodayReadings is recreated each render and re-running would stop/restart AutoSend on every render
  }, [startAutoSend, stopAutoSend]);

  const contextValue = {
    // State
    syncState,
    isAutoSendActive:
      syncState === BLE_SYNC_STATE.AUTOSEND_LISTENING ||
      syncState === BLE_SYNC_STATE.AUTOSEND_RECEIVING ||
      syncState === BLE_SYNC_STATE.AUTOSEND_SUCCESS,
    isReceivingData,
    lastReading,
    readingsVersion, // Increments when new readings are synced - watch this to refresh UI

    // AutoSend-first UI state
    todayReadings, // All readings from today
    lastSyncTime, // Timestamp of last successful sync
    syncStats, // {todayCount, totalSynced}

    // AutoSend control
    startAutoSend,
    stopAutoSend,
    pauseAutoSend,
    resumeAutoSend,
    clearDeviceVerification, // Call when removing/re-pairing a device
    markDeviceAsRecentlyPaired, // Call after pairing completes

    // Manual sync coordination
    requestManualSyncLock,
    releaseManualSyncLock,

    // Force sync (for DeviceDetailView)
    forceSync,

    // Refresh today's readings
    loadTodayReadings,
  };

  return <Provider value={contextValue}>{props.children}</Provider>;
};

export default BLESyncContext;
